import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { gridSizeMdLg6, ROUTER_PATHS, useCheckPermissionsArray, useLoaders } from '@/shared';
import {
  useFetchAllCountries,
  useFetchCitiesEnabled,
  useFetchProvinces,
  useFetchZones,
} from '@/shared/actions/erp';
import {
  CreateSectorParams,
  useCreateSector,
  useUpdateSector,
} from '@/shared/actions/erp/sectors.actions';
import {
  CustomAutocomplete,
  CustomTextField,
  SampleCheckbox,
  SingleFormBoxScene,
} from '@/shared/components/ui';
import { AlertSeverityEnum } from '@/shared/interfaces';
import {
  City,
  Country,
  Province,
} from '@/shared/interfaces/erp';
import { newSectorSchema } from '@/shared/utils';
import { useUiStore } from '@/store/ui';
import { SAVE_SECTOR_PERMISSIONS } from '@/erp/sytem-sttings/sectors/shared/constants';
import { Sector, Zone } from '@/shared/interfaces/erp/mantenimiento-operaciones';

export type SaveSectorProps = {
  titlePage: string;
  editSector?: Sector;
  enableUploadFile?: boolean;
};

export type SaveFormData = CreateSectorParams & {};

const SaveSector: React.FC<SaveSectorProps> = ({ titlePage, editSector }) => {
  useCheckPermissionsArray(SAVE_SECTOR_PERMISSIONS);
  const returnUrlSectorsPage =
    ROUTER_PATHS.mantenimientoOperaciones.sectoresNav;
  const navigate = useNavigate();
  const [shrink, setShrink] = useState(false);

  ///* global state ---------------------
  const setAlert = useUiStore(s => s.setAlert);

  ///* form ---------------------
  const form = useForm<SaveFormData>({
    resolver: yupResolver(newSectorSchema) as any,
    defaultValues: {
      estado: true,
      cobertura: true,
    },
  });
  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, isValid },
  } = form;

  const watchedCountry = form.watch('pais');
  const watchedProvince = form.watch('provincia');
  const watchedCity = form.watch('ciudad');

  ///* fetch data ---------------------
  const {
    data: countriesPaging,
    isLoading: isLoadingCountries,
    isRefetching: isRefetchingCountries,
  } = useFetchAllCountries({
    page_size: 600,
  });
  const {
    data: provincesPaging,
    isLoading: isLoadingProvinces,
    isRefetching: isRefetchingProvinces,
  } = useFetchProvinces(
    {
      page_size: 600,
      pais: watchedCountry,
    },
    !!watchedCountry
  );
  const {
    data: citiesPaging,
    isLoading: isLoadingCities,
    isRefetching: isRefetchingCities,
  } = useFetchCitiesEnabled(
    {
      params: {
        page_size: 600,
        provincia: watchedProvince,
        pais: watchedCountry,
      },
      enabled: !!watchedProvince && !!watchedCountry
    }
  );
  const {
    data: zonesPaging,
    isLoading: isLoadingZones,
    isRefetching: isRefetchingZones,
  } = useFetchZones(
    {
      page_size: 600,
      ciudad: watchedCity,
    },
    !!watchedCity
  );



  


  ///* mutations ---------------------
  const createSectorMutation = useCreateSector({
    navigate,
    returnUrl: returnUrlSectorsPage,
  });
  const updateSectorMutation = useUpdateSector({
    navigate,
    returnUrl: returnUrlSectorsPage,
  });

  ///* handlers ---------------------
  const onSave = () => {
    if (!isValid) return;

    ///* edit
    if (editSector?.id_sector) {
      return updateSectorMutation.mutate({
        id: editSector.id_sector,
        data: form.getValues(),
      });
    }

    ///* create
    createSectorMutation.mutate(form.getValues());
  };

  ///* effects ---------------------
  useEffect(() => {
    if (!editSector?.id_sector) return;
    setShrink(true);

    reset(editSector);
  }, [editSector]);

  /// alerts ------
  // province
  useEffect(() => {
    if (isLoadingProvinces || isRefetchingProvinces) return;

    if (watchedCountry && !provincesPaging?.data?.data?.length) {
      setAlert(
        true,
        'No se encontraron provincias disponibles',
        AlertSeverityEnum.warning
      );
    }
  }, [
    watchedCountry,
    provincesPaging,
    isLoadingProvinces,
    isRefetchingProvinces,
  ]);
  // city
  useEffect(() => {
    if (isLoadingCities || isRefetchingCities) return;

    if (watchedProvince && !citiesPaging?.data?.data?.length) {
      setAlert(
        true,
        'No se encontraron ciudades disponibles',
        AlertSeverityEnum.warning
      );
    }
  }, [watchedProvince, citiesPaging, isLoadingCities, isRefetchingCities]);
  // zone
  useEffect(() => {
    if (isLoadingZones || isRefetchingZones) return;

    if (watchedCity && !zonesPaging?.data?.data?.length) {
      setAlert(
        true,
        'No se encontraron zonas disponibles',
        AlertSeverityEnum.warning
      );
    }
  }, [watchedCity, zonesPaging, isLoadingZones, isRefetchingZones]);

  useLoaders(
    isLoadingProvinces ||
    isLoadingCountries ||
    isRefetchingProvinces ||
    isRefetchingCountries ||
    isLoadingCities ||
    isRefetchingCities ||
    isLoadingZones ||
    isRefetchingZones
  );

  if (
    editSector &&
    (isLoadingCountries ||
      isLoadingProvinces ||
      isLoadingCities ||
      isLoadingZones)
  )
    return null;

  return (
    <SingleFormBoxScene
      titlePage={titlePage}
      onCancel={() => {
        navigate(returnUrlSectorsPage);
      }}
      onSave={handleSubmit(onSave)}
      
    >
      <CustomTextField
        label="Nombre"
        name="nombre"
        control={form.control}
        defaultValue={form.getValues().nombre || ''}
        error={errors.nombre}
        helperText={errors.nombre?.message}
        shrink={shrink}
      />

      <CustomAutocomplete<Country>
        label="País"
        name="pais"
        // options
        options={countriesPaging?.data?.data || []}
        valueKey="nombre"
        actualValueKey="codigo"
        defaultValue={form.getValues().pais || ''}
        optionLabelForEdit={form.getValues().pais || ''}
        isLoadingData={isLoadingCountries}
        // validation
        control={control}
        error={errors.pais}
        helperText={errors.pais?.message}
        size={gridSizeMdLg6}
        onChangeValue={() => {
          form.setValue('provincia', '');
          form.setValue('ciudad', '');
          form.setValue('zona', '');
        }}
      />
      <CustomAutocomplete<Province>
        label="Provincia"
        name="provincia"
        // options
        options={provincesPaging?.data?.data || []}
        valueKey="nombre"
        actualValueKey="codigo"
        defaultValue={form.getValues().provincia || ''}
        optionLabelForEdit={form.getValues().provincia || ''}
        isLoadingData={isLoadingProvinces}
        // validation
        control={control}
        error={errors.provincia}
        helperText={errors.provincia?.message}
        size={gridSizeMdLg6}
        onChangeValue={() => {
          form.setValue('ciudad', '');
          form.setValue('zona', '');
        }}
      />
      <CustomAutocomplete<City>
        label="Ciudad"
        name="ciudad"
        // options
        options={citiesPaging?.data?.data || []}
        valueKey="nombre"
        actualValueKey="codigo"
        defaultValue={form.getValues().ciudad || ''}
        optionLabelForEdit={form.getValues().ciudad}
        isLoadingData={isLoadingCities}
        // validation
        control={control}
        error={errors.ciudad}
        helperText={errors.ciudad?.message}
        size={gridSizeMdLg6}
        onChangeValue={() => {
          form.setValue('zona', '');
        }}
      />
      <CustomAutocomplete<Zone>
        label="Zona"
        name="zona"
        // options
        options={zonesPaging?.data?.data || []}
        valueKey="nombre"
        actualValueKey="codigo"
        defaultValue={form.getValues().zona || ''}
        optionLabelForEdit={form.getValues().zona}
        isLoadingData={isLoadingZones}
        // validation
        control={control}
        error={errors.zona}
        helperText={errors.zona?.message}
        size={gridSizeMdLg6}
      />

      <SampleCheckbox
        label="Tiene cobertura"
        name="cobertura"
        control={form.control}
        defaultValue={!!form.getValues().cobertura}
        size={gridSizeMdLg6}
      />
      <SampleCheckbox
        label="Estado"
        name="estado"
        control={form.control}
        defaultValue={!!form.getValues().estado}
        isState
      />
    </SingleFormBoxScene>
  );
};

export default SaveSector;